<script setup lang="ts">
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName, TrackingCopilotSource } from '@/core/shared/tracking/tracking-actions';
import ProjectService from '@/core/shared/project/project.service';
import ProjectSearchFilters from '@/components/Shared/Projects/ProjectSearchFilters.vue';
import { PageSource } from '@/core/shared/tracking/tracking.type';
import { type LocationQuery, useRoute, useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { omit } from 'radash';
import { ErrorService } from '@/core/shared/errors/error.service';
import { LocationNotFoundError } from '@/core/shared/errors/location-not-found.error';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectRemovedFromDeeplink from '@/components/Shared/Projects/ProjectRemovedFromDeeplink.vue';
import ProjectDescriptionOverlay from '@/components/Shared/Projects/ProjectDescriptionOverlay.vue';
import ProjectList from '@/components/Shared/Projects/ProjectList.vue';
import MeService from '@/core/shared/me/me.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import PlusSvg from '@/assets/svg/plus-16.svg?component';
import ConfirmJobCreationWithIntegration from '@/components/Jobs/CopilotActivation/ConfirmJobCreationWithIntegration.vue';
import AtsConnectService from '@/core/shared/ats-connect/ats-connect.service';
import { hasDeepLinkQuery, withoutDeepLinkQuery } from '@/composables/jobs/deepLinkQuery';

const route = useRoute();
const router = useRouter();
const dialogService = new DialogService();
const projectService = new ProjectService();
const meService = new MeService();
const atsConnectService = new AtsConnectService();

const isCreatingJob = ref<boolean>(false);
const isIntegrationWarningDialogOpen = ref(false);
const hasShownIntegrationDialog = ref(false);

const hasProjects = computed(() => {
  return projectService.projects.length > 0;
});

const pageSource = computed<PageSource>(() => {
  if (typeof route.name !== 'string') {
    return PageSource.Conversations;
  }

  const lowerCaseRouteName = route.name.toLowerCase();
  const includesSourcing = lowerCaseRouteName.includes('sourcing');

  if (includesSourcing) {
    return PageSource.Sourcing;
  }

  return PageSource.Conversations;
});

async function checkForActiveIntegration() {
  try {
    const hasActiveIntegration = await atsConnectService.hasActiveIntegration();
    return hasActiveIntegration;
  } catch (error) {
    ErrorService.captureException(error);
    return false;
  }
}

async function createJobProject() {
  isCreatingJob.value = true;

  const hasActiveIntegration = await checkForActiveIntegration();
  if (hasActiveIntegration && !hasShownIntegrationDialog.value) {
    isIntegrationWarningDialogOpen.value = true;
    hasShownIntegrationDialog.value = true;
    isCreatingJob.value = false;
    return;
  }

  await handleJobCreationFlow();
}

async function handleJobCreationFlow() {
  try {
    if (pageSource.value === PageSource.Sourcing) {
      TrackingService.trackAction(TrackingActionName.NEW_SEARCH_STARTED, {
        source: TrackingCopilotSource.PLUS_NEW_SEARCH_BUTTON,
      });
      dialogService.isCreateProjectDialogOpen = true;
    } else {
      await createNewProjectAndJob();
    }
  } catch (error) {
    if (error instanceof LocationNotFoundError) {
      SnackbarService.critical(
        `We couldn't find your company location "${error.location}". ` +
          'Update the company address, or start from New search and pick a city.',
      );
    } else {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to create job. Please try again later.');
    }
  } finally {
    isCreatingJob.value = false;
  }
}

async function handleFiltersChange() {
  try {
    projectService.repairMyJobsTeamFilter();

    await projectService.searchProjects({
      skipOnSameSearch: true,
      skipTracking: false,
      pageSource: pageSource.value,
    });

    await projectService.searchAvailableProjects();
    await handleProjectsInQueryParams();
    await handleNoSelectedProjectState();
    projectService.didLoadProjects = true;
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to load jobs');
  }
}

// The first search belongs to the service; only its deep-link and auto-select
// side effects are re-run here, so this must not issue a search of its own.
async function handleInitialSearchDone() {
  try {
    projectService.repairMyJobsTeamFilter();
    await handleProjectsInQueryParams();
    await handleNoSelectedProjectState();
  } catch (error) {
    ErrorService.captureException(error);
  }
}

async function handleProjectsInQueryParams() {
  const query = route.query;
  let projects: number[] = [];

  if (typeof query.projects === 'string') {
    projects = [Number(query.projects)];
  }

  if (Array.isArray(query.projects)) {
    projects = query.projects.map(Number);
  }

  const validProjectIds = projects.filter((id) => !isNaN(id));

  if (validProjectIds.length === 0) {
    return;
  }

  // Consumed here: kept in the URL, every later filter event would re-open the job.
  await handleProjectChange(validProjectIds[0], omit(query, ['projects']));
}

async function handleNoSelectedProjectState() {
  // Only auto-navigate on sourcing pages where project selection is required
  // For all other pages, let them handle their own navigation
  const isSourcingPage = route.path.startsWith('/sourcing');

  if (!isSourcingPage) {
    // Don't auto-navigate on any non-sourcing page
    return;
  }

  if (hasProjects.value && !projectService.currentProject?.id) {
    await handleProjectChange(projectService.projects[0].id);
  }
}

// A click is a fresh choice, so a deep link still pending in the URL must not override it.
function handleProjectClick(projectId: number) {
  return handleProjectChange(projectId, withoutDeepLinkQuery(route.query));
}

async function handleProjectChange(projectId: number, query: LocationQuery = route.query) {
  // Preserve existing query params, especially applicationStatus
  const currentQuery = { ...query };

  if (pageSource.value === PageSource.Sourcing) {
    // Navigate to sourcing page - let it handle the project loading
    await router.push({
      name: 'sourcing-job-id',
      params: { id: `${projectId}` },
      query: currentQuery,
    });
  } else {
    // Navigate to jobs page - let it handle the project loading via route params
    await router.push({
      path: `/jobs/${projectId}/candidates`,
      query: currentQuery,
    });
  }
}

const companyLocation = computed(() => {
  let location = '';
  location += meService.employer?.city ? `${meService.employer?.city}, ` : '';
  location += meService.employer?.state ? `${meService.employer?.state}, ` : '';
  location += `USA`;
  return location;
});

const newButtonText = computed(() => {
  return pageSource.value === PageSource.Sourcing ? 'New search' : 'New job';
});

const currentProjectId = computed(() => projectService.currentProject?.id);

// The current job lags the URL while the next job's lookup is in flight, so a click
// back to it must be judged against where the route is headed.
const routeProjectId = computed(() => {
  const param = route.params.projectId;
  const id = Number(param);
  return typeof param === 'string' && param !== '' && !Number.isNaN(id) ? id : undefined;
});

// With no job in the URL the route rests on the loaded job, unless a pending deep link is
// taking it to a job not known yet; a click must then always navigate to cancel it.
const clickTargetProjectId = computed(() => {
  if (routeProjectId.value !== undefined) return routeProjectId.value;
  return hasDeepLinkQuery(route.query) ? undefined : currentProjectId.value;
});

async function createNewProjectAndJob() {
  const project = await projectService.createProject({
    title: 'No title',
    location: companyLocation.value,
    activeOutreachEnabled: true,
  });

  TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_STARTED, {
    source: TrackingCopilotSource.PLUS_NEW_JOB_BUTTON,
    project_id: project.id,
    job_id: project.jobId,
  });

  // Lets give enough time to the Project side effect to create the new Job
  setTimeout(async () => {
    isCreatingJob.value = false;
    await router.push(`/jobs/${project.id}/copilot-activation?isCreatingJob=true`);
  }, 500);
}

async function confirmJobCreationWithIntegration() {
  isIntegrationWarningDialogOpen.value = false;
  isCreatingJob.value = true;

  hasShownIntegrationDialog.value = false;
  await handleJobCreationFlow();
}

function cancelJobCreationWithIntegration() {
  isIntegrationWarningDialogOpen.value = false;
  hasShownIntegrationDialog.value = false;
  isCreatingJob.value = false;
}
</script>
<template>
  <!-- New Job Button -->
  <div class="flex items-center justify-between px-4 pb-[2px] pt-[18px]">
    <div class="font-serif text-lg font-black leading-6">Saved jobs</div>
    <v-btn
      :ripple="false"
      :disabled="isCreatingJob"
      :loading="isCreatingJob"
      variant="flat"
      class="add-job-button"
      @click="createJobProject"
    >
      <PlusSvg class="mr-1" />
      {{ newButtonText }}
    </v-btn>
  </div>
  <!-- Project Search Filters -->
  <div class="mx-4 mb-4 mt-[14px]">
    <ProjectSearchFilters
      @change:filters="handleFiltersChange"
      @initial-search:done="handleInitialSearchDone"
    />
  </div>

  <!-- Project List -->
  <div
    v-if="!projectService.isProjectFromDeepLinkRemoved"
    class="jobs-drawer-scrollbar menu-scrollbar menu-scrollbar--tight mt-2 h-[calc(100vh-204px)] overflow-y-auto pl-4 pr-[9px]"
  >
    <ProjectList
      :project-id="currentProjectId"
      :target-project-id="clickTargetProjectId"
      @change:project-id="handleProjectClick"
      @add-job="createJobProject"
    />
  </div>

  <div
    v-if="projectService.isProjectFromDeepLinkRemoved && !projectService.isSearchingProjects"
    class="px-1 pb-2"
  >
    <ProjectRemovedFromDeeplink />
  </div>

  <ProjectDescriptionOverlay />

  <ConfirmJobCreationWithIntegration
    :is-open="isIntegrationWarningDialogOpen"
    :is-loading="isCreatingJob"
    @create="confirmJobCreationWithIntegration"
    @cancel="cancelJobCreationWithIntegration"
  />
</template>

<style scoped>
/* The shared tight pill, recoloured for the dark panel. The 9px right padding makes the cards 300px
   wide in the 336px drawer. */
.jobs-drawer-scrollbar {
  --menu-scrollbar-thumb: theme('colors.shade.800');
  --menu-scrollbar-thumb-hover: theme('colors.shade.600');
}
.add-job-button {
  @apply !h-7 !min-h-0 bg-highlight-500 !px-2 !py-0 !text-xs !font-bold;
  @apply !rounded-md !normal-case leading-4 !tracking-normal text-white;
  @apply !transition-colors !duration-300;
}
</style>
